/**
 * The request-slots dialog (§5.1.1, §18.6's SLOTS sibling), opened over Créditos by the
 * `Pedir vagas` button, laid out like `RequestCreditsDialog`: the slots held and in use now, how
 * many more (a stepper, with the total this request leads to beside the current figures), and an
 * optional reason.
 *
 * `capacity` arrives as a prop from the page's own `useCapacity` call rather than being fetched
 * again here; the request list and the create mutation are this component's own.
 *
 * The API refuses a second open request of the same kind with a 409. An already-open request
 * renders as a read-only notice with no form.
 */

import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { Minus, Plus } from 'lucide-react';

import { IconHint } from '../../../chrome/hint';
import { DialogSurfaceFooter, FormDialog } from '../../../chrome/form';
import { DefinitionList } from '../../../chrome/page';
import { getLocale } from '../../../i18n';
import { integer } from '../../../i18n/format';
import { ft } from '../../../chrome/form/messages';
import { Button } from '../../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../ui/dialog';
import { Field, FieldLabel } from '../../../ui/field';
import { Input } from '../../../ui/input';
import { Textarea } from '../../../ui/textarea';
import type { Capacity } from './api/schemas';
import { openRequestOf, useCapacityRequests, useCreateCapacityRequest } from './api/queries';
import { cm } from './messages';

const MIN_QUANTITY = 1;

export function RequestSlots(props: {
  tenantId: string;
  capacity: Capacity;
  trigger: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}): ReactNode {
  const { tenantId, capacity, trigger } = props;
  const quantityId = useId();
  const contextId = useId();

  const [ownOpen, setOwnOpen] = useState(false);
  const open = props.open ?? ownOpen;
  const setOpen = props.onOpenChange ?? setOwnOpen;
  const [quantity, setQuantity] = useState(MIN_QUANTITY);
  const [context, setContext] = useState('');

  const requestsQuery = useCapacityRequests(tenantId);
  const createRequest = useCreateCapacityRequest(tenantId);

  const openRequest = openRequestOf(requestsQuery.data ?? [], 'SLOTS');

  function clampQuantity(next: number): void {
    setQuantity(Math.max(MIN_QUANTITY, Math.trunc(next)));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void | Promise<unknown> {
    event.preventDefault();
    return createRequest
      .mutateAsync({
        kind: 'SLOTS',
        credits_requested: null,
        slots_requested: quantity,
        accounts_to_track: null,
        context: context.trim(),
      })
      .then(() => {
        setQuantity(MIN_QUANTITY);
        setContext('');
      });
  }

  if (openRequest !== undefined) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{cm('request.pending.title')}</DialogTitle>
          </DialogHeader>
          <p className="text-body-sm text-muted-foreground">{cm('request.pending.body')}</p>
          <DialogSurfaceFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {ft('close')}
            </Button>
          </DialogSurfaceFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const locale = getLocale();
  const current = capacity.campaign_slots ?? 0;

  return (
    <FormDialog
      open={open}
      onOpenChange={setOpen}
      trigger={trigger}
      title={cm('request.title')}
      submitLabel={cm('request.submit')}
      pending={createRequest.isPending}
      failure={createRequest.error?.failure ?? null}
      onSubmit={handleSubmit}
    >
      <DefinitionList
        columns={3}
        items={[
          { term: cm('request.current'), detail: integer(current, locale) },
          { term: cm('request.inUse'), detail: integer(capacity.campaigns_in_use, locale) },
          { term: cm('request.after'), detail: integer(current + quantity, locale) },
        ]}
      />

      <Field>
        <FieldLabel htmlFor={quantityId}>{cm('request.quantity.label')}</FieldLabel>
        {/* `Field`'s vertical orientation stretches every direct child to full width
            (`[&>*]:w-full` in `ui/field.tsx`) — `w-fit` alone loses that cascade, `self-start`
            drops the child out of the stretched track. */}
        <div className="inline-flex w-fit items-center self-start rounded-md border">
          <IconHint label={cm('request.quantity.decrement')}>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={quantity <= MIN_QUANTITY}
              onClick={() => clampQuantity(quantity - 1)}
            >
              <Minus aria-hidden />
            </Button>
          </IconHint>
          <Input
            id={quantityId}
            type="number"
            inputMode="numeric"
            min={MIN_QUANTITY}
            value={quantity}
            onChange={(event) => clampQuantity(Number(event.target.value))}
            className="w-16 border-0 text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
          <IconHint label={cm('request.quantity.increment')}>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => clampQuantity(quantity + 1)}
            >
              <Plus aria-hidden />
            </Button>
          </IconHint>
        </div>
      </Field>

      <Field>
        <FieldLabel htmlFor={contextId}>{cm('request.context.label')}</FieldLabel>
        <Textarea
          id={contextId}
          value={context}
          onChange={(event) => setContext(event.target.value)}
        />
      </Field>
    </FormDialog>
  );
}
